/**
 * After `bridge_identified`: push cached `agent_capabilities_batch` immediately, await probes only for
 * agent types missing SQLite cache, then lazily re-probe all types in the background and send another
 * batch when the probed payload hash changes.
 */

import WebSocket from 'ws';
import * as path from 'node:path';
import type { LogFn } from '../../log.js';
import { sendWsMessage } from '../../connection/create-ws-bridge.js';
import { getBridgeRoot } from '../../files/cwd/index.js';
import { detectLocalAgentTypes } from '../detect-local-agent-types.js';
import { probeAgentCapabilitiesForDetectedTypes } from './probe-agent-capabilities-for-types.js';
import { listCliAgentCapabilityCacheForWorkspace } from '../../sqlite/agent-capability-cache.js';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { CliSqliteInterrupted, withCliSqlite } from '../../sqlite/cli-database.js';
import { beginAgentCapabilityWarmupRun } from './agent-capability-warmup-run.js';

export async function warmupAgentCapabilitiesOnConnect(params: {
  workspaceId: string;
  log: LogFn;
  getWs: () => WebSocket | null;
}): Promise<void> {
  const { workspaceId, log, getWs } = params;
  const isCurrent = beginAgentCapabilityWarmupRun();
  if (!isCurrent()) return;
  const cwd = path.resolve(getBridgeRoot());

  async function sendBatchFromCache(): Promise<void> {
    if (!isCurrent()) return;
    const socket = getWs();
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      const rows = await withCliSqlite((db) => listCliAgentCapabilityCacheForWorkspace(db, workspaceId));
      if (!isCurrent()) return;
      if (rows.length === 0) return;
      sendWsMessage(socket, {
        type: 'agent_capabilities_batch',
        items: rows.map((r) => ({ agentType: r.agentType, configOptions: r.configOptions })),
      });
    } catch (e) {
      if (e instanceof CliSqliteInterrupted) return;
      log(
        `[Bridge service] Agent capability batch to bridge failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  }

  // 1. Cached rows only — no blocking probes.
  await sendBatchFromCache();
  if (!isCurrent()) return;

  let types: string[] = [];
  try {
    types = [...(await detectLocalAgentTypes())];
  } catch (e) {
    log(`[Bridge service] detectLocalAgentTypes failed: ${e instanceof Error ? e.message : String(e)}`);
  }
  if (!isCurrent()) return;

  // 2. Blocking: only types missing a non-empty SQLite snapshot.
  try {
    const n = await probeAgentCapabilitiesForDetectedTypes({
      agentTypes: types,
      cwd,
      workspaceId,
      log,
      bridgeReport: false,
      forceAllTypes: false,
      shouldContinue: isCurrent,
    });
    if (n > 0) await sendBatchFromCache();
  } catch (e) {
    if (e instanceof CliSqliteInterrupted) return;
    log(`[Bridge service] Agent capability probe (missing cache) failed: ${e instanceof Error ? e.message : String(e)}`);
  }
  if (!isCurrent()) return;

  // 3. Lazy refresh: re-probe all types; upserts are hash-gated so we only send when something changed.
  void (async () => {
    try {
      await yieldToEventLoop();
      if (!isCurrent()) return;
      const n = await probeAgentCapabilitiesForDetectedTypes({
        agentTypes: types,
        cwd,
        workspaceId,
        log,
        bridgeReport: false,
        forceAllTypes: true,
        shouldContinue: isCurrent,
      });
      if (n > 0) await sendBatchFromCache();
    } catch (e) {
      if (e instanceof CliSqliteInterrupted) return;
      log(`[Bridge service] Agent capability lazy refresh failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  })();
}
